import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Download, Loader2, RotateCcw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../api/client';
import ConfirmModal from '../ui/ConfirmModal';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { fmtCurrency } from '../../lib/display-labels';
import { saveBlobAs } from '../../lib/blob-tab';
import {
  REPORT_BUCKET_LABELS,
  parsePersonReport,
  parseReportRollUp,
  reimbursementPathLabel,
  shiftMonth,
  type CostFigures,
  type ReportRow,
} from '../../lib/expenses';

/**
 * The monthly reimbursement report (FE-5, over api#1030's GET /expenses/report).
 *
 * THE MONTH. The first load sends NO month — the API decides "this month" from
 * the tenant's zone (companies.time_zone), so this screen never guesses one from
 * the browser. Prev / next then shift the month the API returned, as YYYY-MM
 * strings built from their parts (shiftMonth), never a Date.
 *
 * EVERY FIGURE IS THE API'S. "Owed" is the API's amount_owed — approved,
 * report-path, not yet marked reimbursed — and is never summed here, so the
 * number on screen is the number on the PDF. Absent renders "—", never $0.00.
 *
 * MARK REIMBURSED is POST /expenses/:id/reimbursed, offered ONLY on an `owed`
 * row (a payroll-path row is paid through its pay period; marking it would
 * record a second payment), with Undo on a `reimbursed` row. The bulk action
 * marks exactly the rows the API bucketed `owed`, one call each — the API has
 * no bulk door — after a confirmation naming the API's count and total.
 */
export interface ReimbursementReportPanelProps {
  /** Office view roles get the roll-up and a person picker; everyone else sees only themself. */
  officeView: boolean;
  canMarkReimbursed: boolean;
  currentUserId: string | null;
  people: Array<{ id: string; name: string }>;
}

export default function ReimbursementReportPanel({ officeView, canMarkReimbursed, currentUserId, people }: ReimbursementReportPanelProps) {
  const queryClient = useQueryClient();
  // null = let the API choose the tenant's current month.
  const [month, setMonth] = useState<string | null>(null);
  // '' = the office roll-up. A non-office reader is always looking at themself.
  const [personId, setPersonId] = useState<string>(officeView ? '' : (currentUserId ?? ''));
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [downloading, setDownloading] = useState<'csv' | 'pdf' | null>(null);

  const params = { ...(month ? { month } : {}), ...(personId ? { user_id: personId } : {}) };

  const query = useQuery({
    queryKey: ['expenses', 'report', personId || 'roll-up', month ?? 'current'],
    queryFn: () =>
      api.expenses.report(params).then((r) => (personId ? { kind: 'person' as const, data: parsePersonReport(r.data) } : { kind: 'rollup' as const, data: parseReportRollUp(r.data) })),
    enabled: officeView || !!currentUserId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const shownMonth = query.data?.data?.month ?? month;
  const monthLabel = query.data?.data?.monthLabel ?? shownMonth ?? '';

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['expenses'] });
  const markOne = useMutation({
    mutationFn: ({ id, undo }: { id: string; undo: boolean }) =>
      undo ? api.expenses.unmarkReimbursed(id) : api.expenses.markReimbursed(id),
    onSuccess: (_r, { undo }) => toast.success(undo ? 'Mark removed — owed again' : 'Marked reimbursed'),
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not change the reimbursed mark.')),
    onSettled: refresh,
  });
  const markAll = useMutation({
    // One call per owed id, in order; the API has no bulk door. Stops at the
    // first refusal so the screen never claims more than was recorded.
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await api.expenses.markReimbursed(id);
      return ids.length;
    },
    onSuccess: (n) => toast.success(`Marked ${n} receipt${n === 1 ? '' : 's'} reimbursed`),
    onError: (err) => toast.error(getApiErrorMessage(err, 'Stopped: a receipt could not be marked reimbursed.')),
    onSettled: () => { setConfirmBulk(false); refresh(); },
  });

  const download = async (format: 'csv' | 'pdf') => {
    setDownloading(format);
    try {
      const res = await api.expenses.reportFile(params, format);
      saveBlobAs(res.data as Blob, `reimbursement-${personId ? 'person' : 'all'}-${shownMonth ?? 'current'}.${format}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Could not download the ${format.toUpperCase()}.`));
    } finally {
      setDownloading(null);
    }
  };

  const money = (f: CostFigures | undefined) => fmtCurrency(f?.gross ?? null);
  const person = query.data?.kind === 'person' ? query.data.data : null;
  const rollup = query.data?.kind === 'rollup' ? query.data.data : null;
  const owedIds = person ? person.rows.filter((r) => r.bucket === 'owed').map((r) => r.id) : [];

  return (
    <section data-testid="reimbursement-report" aria-labelledby="reimbursement-report-title"
      className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Reimbursement report</p>
          <h2 id="reimbursement-report-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
            {monthLabel || 'This month'}
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" aria-label="Previous month" disabled={!shownMonth}
            onClick={() => shownMonth && setMonth(shiftMonth(shownMonth, -1))}
            className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card p-1.5 text-fieldiq-text disabled:opacity-50">
            <ChevronLeft size={14} />
          </button>
          <span data-testid="report-month" className="min-w-[5.5rem] text-center text-xs font-bold tabular-nums text-fieldiq-text">{shownMonth ?? '—'}</span>
          <button type="button" aria-label="Next month" disabled={!shownMonth}
            onClick={() => shownMonth && setMonth(shiftMonth(shownMonth, 1))}
            className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card p-1.5 text-fieldiq-text disabled:opacity-50">
            <ChevronRight size={14} />
          </button>
          {officeView && (
            <select aria-label="Report for" value={personId} onChange={(e) => setPersonId(e.target.value)}
              className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-2 py-1 text-xs text-fieldiq-text focus:border-fi-amber outline-none">
              <option value="">Everyone (who do we owe)</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <button type="button" onClick={() => download('csv')} disabled={downloading !== null || !!failure}
            className="fi-action fi-action--compact border border-fieldiq-border bg-fieldiq-card text-fieldiq-text disabled:opacity-50">
            {downloading === 'csv' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} CSV
          </button>
          <button type="button" onClick={() => download('pdf')} disabled={downloading !== null || !!failure}
            className="fi-action fi-action--compact border border-fieldiq-border bg-fieldiq-card text-fieldiq-text disabled:opacity-50">
            {downloading === 'pdf' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} PDF
          </button>
        </div>
      </header>

      {query.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-fieldiq-muted">
          <Loader2 size={18} className="animate-spin" /> Loading report…
        </div>
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the reimbursement report"
          allowed="Admins, office staff and dispatchers (for anyone's report)" />
      ) : rollup ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-3">Person</th>
                <th scope="col" className="px-4 py-3 text-right text-fi-signal-amber">Owed</th>
                <th scope="col" className="px-4 py-3 text-right">Reimbursed</th>
                <th scope="col" className="px-4 py-3 text-right">Payroll</th>
                <th scope="col" className="px-4 py-3 text-right">Company-paid</th>
                <th scope="col" className="px-4 py-3 text-right">Pending</th>
              </tr>
            </thead>
            <tbody>
              {rollup.people.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-fieldiq-muted">No expenses this month.</td></tr>
              ) : rollup.people.map((p) => (
                <tr key={p.userId} data-testid={`rollup-row-${p.userId}`} className="border-t border-fieldiq-border">
                  <td className="px-4 py-3 text-fieldiq-text">{p.name ?? '—'}</td>
                  <td className="px-4 py-3 text-right text-base font-extrabold tabular-nums text-fieldiq-text" data-testid={`rollup-owed-${p.userId}`}>{money(p.owed)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-fieldiq-text">{money(p.reimbursed)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-fieldiq-text">{money(p.payroll)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-fieldiq-text">{money(p.company)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-fieldiq-text">{money(p.pending)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-fieldiq-border">
              <tr>
                <td className="px-4 py-3 font-bold text-fieldiq-text">Total owed</td>
                <td className="px-4 py-3 text-right text-base font-extrabold tabular-nums text-fieldiq-text" data-testid="rollup-total-owed">{money(rollup.amountOwed)}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </div>
      ) : person ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <p className="text-sm text-fieldiq-text">
              Amount owed{person.person?.name ? ` to ${person.person.name}` : ''}:{' '}
              <strong data-testid="person-amount-owed" className="text-lg tabular-nums">{money(person.amountOwed)}</strong>
              <span className="ml-2 text-xs text-fieldiq-muted">
                {person.amountOwed.count ?? '—'} receipt{person.amountOwed.count === 1 ? '' : 's'} on the report, not yet reimbursed
              </span>
            </p>
            {canMarkReimbursed && owedIds.length > 0 && (
              <button type="button" onClick={() => setConfirmBulk(true)} className="fi-action fi-action--primary">
                <CheckCircle2 size={14} /> Mark all reimbursed
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-left text-sm">
              <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                <tr>
                  <th scope="col" className="px-4 py-3">Date</th>
                  <th scope="col" className="px-4 py-3">Vendor</th>
                  <th scope="col" className="px-4 py-3">For</th>
                  <th scope="col" className="px-4 py-3 text-right">Amount</th>
                  <th scope="col" className="px-4 py-3 text-right">GST</th>
                  <th scope="col" className="px-4 py-3">Status</th>
                  <th scope="col" className="px-4 py-3">Path</th>
                  {canMarkReimbursed && <th scope="col" className="px-4 py-3 text-right">Reimbursed</th>}
                </tr>
              </thead>
              <tbody>
                {person.rows.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-fieldiq-muted">No expenses this month.</td></tr>
                ) : person.rows.map((r) => (
                  <ReportRowView key={r.id} row={r} canMark={canMarkReimbursed}
                    busy={markOne.isPending || markAll.isPending}
                    onMark={(undo) => markOne.mutate({ id: r.id, undo })} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 text-xs text-fieldiq-muted">
            {(['reimbursed', 'payroll', 'company', 'pending', 'rejected'] as const).map((k) => (
              <span key={k} className="mr-4">{REPORT_BUCKET_LABELS[k]}: {money(person.byBucket[k])}</span>
            ))}
            {person.gstUnknownNote && <p className="mt-1 text-fi-signal-amber">{person.gstUnknownNote}</p>}
          </div>
          <ConfirmModal
            isOpen={confirmBulk}
            title="Mark all reimbursed?"
            body={`${person.amountOwed.count ?? owedIds.length} receipt${owedIds.length === 1 ? '' : 's'} totalling ${money(person.amountOwed)} will be recorded as paid back outside payroll for ${monthLabel}.`}
            confirmLabel="Record as reimbursed"
            confirmVariant="primary"
            loading={markAll.isPending}
            onConfirm={() => markAll.mutate(owedIds)}
            onCancel={() => setConfirmBulk(false)}
          />
        </>
      ) : (
        <p className="px-4 py-10 text-center text-sm text-fieldiq-muted">No report.</p>
      )}
    </section>
  );
}

function ReportRowView({ row, canMark, busy, onMark }: { row: ReportRow; canMark: boolean; busy: boolean; onMark: (undo: boolean) => void }) {
  const pathLabel = reimbursementPathLabel(row.reimbursementPath, row.bucket === 'reimbursed', row.reimbursedOn);
  return (
    <tr data-testid={`report-row-${row.id}`} className="border-t border-fieldiq-border">
      <td className="px-4 py-3 text-fieldiq-text whitespace-nowrap">{row.expenseDate ?? '—'}</td>
      <td className="px-4 py-3 text-fieldiq-text">{row.vendor ?? '—'}</td>
      <td className="px-4 py-3 text-fieldiq-text">
        {row.expenseKind === 'general' ? 'General' : (row.jobNumber ?? '—')}
        {row.customerName ? ` · ${row.customerName}` : ''}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-fieldiq-text">{fmtCurrency(row.amount)}</td>
      <td className="px-4 py-3 text-right tabular-nums text-fieldiq-text">{fmtCurrency(row.gst)}</td>
      <td className="px-4 py-3 text-fieldiq-text capitalize">{row.status || '—'}</td>
      <td className="px-4 py-3 text-fieldiq-text whitespace-nowrap">{pathLabel ?? '—'}</td>
      {canMark && (
        <td className="px-4 py-3 text-right">
          {/* Only an OWED row can be marked; only a REIMBURSED row can be undone.
              Payroll and company rows never get the control. */}
          {row.bucket === 'owed' && (
            <button type="button" disabled={busy} onClick={() => onMark(false)}
              className="fi-action fi-action--compact fi-action--primary disabled:opacity-50">
              <CheckCircle2 size={12} /> Mark reimbursed
            </button>
          )}
          {row.bucket === 'reimbursed' && (
            <button type="button" disabled={busy} onClick={() => onMark(true)}
              className="fi-action fi-action--compact border border-fieldiq-border bg-fieldiq-card text-fieldiq-text disabled:opacity-50">
              <RotateCcw size={12} /> Undo
            </button>
          )}
        </td>
      )}
    </tr>
  );
}
